// Single source of truth for benchmark-strength copy.
// The orgs/respondents/spread strings MUST match the thresholds in the
// score functions below — update both together.
export const BENCHMARK_STRENGTH = {
    0: {
        label: 'Zeer zwakke benchmark',
        helperPrefix: 'Beperkte data',
        orgs: 'Minder dan 5 organisaties',
        respondents: 'Minder dan 100',
        spread: 'Eén organisatie levert meer dan 40% van de data',
    },
    1: {
        label: 'Redelijke benchmark',
        helperPrefix: 'Redelijke data',
        orgs: '5–9 organisaties',
        respondents: '100–249',
        spread: 'Grootste organisatie levert 25–40% van de data',
    },
    2: {
        label: 'Sterke benchmark',
        helperPrefix: 'Sterke data',
        orgs: '10–19 organisaties',
        respondents: '250–499',
        spread: 'Grootste organisatie levert 10–25% van de data',
    },
    3: {
        label: 'Zeer sterke benchmark',
        helperPrefix: 'Zeer sterke data',
        orgs: '20+ organisaties',
        respondents: '500+',
        spread: 'Geen organisatie levert meer dan 10% van de data',
    },
}

// Score thresholds. The gap helper (getBenchmarkNextLevelGaps) reads these same
// constants, so the "what's needed for the next level" numbers can never drift
// from the scores. Keep the copy strings in BENCHMARK_STRENGTH in sync too.
const ORGANISATIONS_FOR_SCORE = { 1: 5, 2: 10, 3: 20 } // minimum organisations
const RESPONDENTS_FOR_SCORE = { 1: 100, 2: 250, 3: 500 } // minimum respondents
const MAX_SHARE_PCT_FOR_SCORE = { 1: 40, 2: 25, 3: 10 } // maximum largest-share %

const getOrganisationScore = (companyCount) => {
    if (companyCount >= ORGANISATIONS_FOR_SCORE[3]) return 3
    if (companyCount >= ORGANISATIONS_FOR_SCORE[2]) return 2
    if (companyCount >= ORGANISATIONS_FOR_SCORE[1]) return 1
    return 0
}

const getRespondentScore = (respondentCount) => {
    if (respondentCount >= RESPONDENTS_FOR_SCORE[3]) return 3
    if (respondentCount >= RESPONDENTS_FOR_SCORE[2]) return 2
    if (respondentCount >= RESPONDENTS_FOR_SCORE[1]) return 1
    return 0
}

const getLargestShareFraction = (respondentsPerCompany = []) => {
    const totalRespondents = respondentsPerCompany.reduce((sum, item) => sum + item.respondentCount, 0)

    if (!totalRespondents) return 0

    const maxRespondentCount = respondentsPerCompany.reduce(
        (max, item) => Math.max(max, item.respondentCount),
        0
    )

    return maxRespondentCount / totalRespondents
}

const getSpreadScore = (respondentsPerCompany = []) => {
    const totalRespondents = respondentsPerCompany.reduce((sum, item) => sum + item.respondentCount, 0)

    if (!totalRespondents) return 0

    const largestSharePct = getLargestShareFraction(respondentsPerCompany) * 100

    if (largestSharePct <= MAX_SHARE_PCT_FOR_SCORE[3]) return 3
    if (largestSharePct <= MAX_SHARE_PCT_FOR_SCORE[2]) return 2
    if (largestSharePct <= MAX_SHARE_PCT_FOR_SCORE[1]) return 1
    return 0
}

const getLargestShare = (respondentsPerCompany = []) =>
    Math.round(getLargestShareFraction(respondentsPerCompany) * 1000) / 10

const getSpreadExplanation = (spreadScore) => {
    if (spreadScore === 0) return 'zeer scheef verdeeld'
    if (spreadScore === 1) return 'beperkt gespreid'
    if (spreadScore === 2) return 'redelijk gespreid'
    return 'breed gespreid'
}

const getBenchmarkStrength = ({ companyCount = 0, respondentCount = 0, respondentsPerCompany = [] }) => {
    const organisationScore = getOrganisationScore(companyCount)
    const respondentScore = getRespondentScore(respondentCount)
    const spreadScore = getSpreadScore(respondentsPerCompany)
    const finalScore = Math.min(organisationScore, respondentScore, spreadScore)

    return {
        ...BENCHMARK_STRENGTH[finalScore],
        organisationScore,
        respondentScore,
        spreadScore,
        finalScore,
        largestShare: getLargestShare(respondentsPerCompany),
        spreadLabel: getSpreadExplanation(spreadScore),
    }
}

/**
 * @typedef {object} BenchmarkNextLevelGaps
 * @property {number|null} organisations - Extra organisations needed for the next level (positive), or null if organisations aren't holding the level back.
 * @property {number|null} respondents - Extra respondents needed for the next level (positive), or null if respondents aren't holding the level back.
 * @property {number|null} largestShare - Percentage points the largest share must drop for the next level (negative), or null if spread isn't holding the level back.
 */

/**
 * How much each dimension that is CURRENTLY holding a benchmark back must change
 * to lift the row one strength level. Because the level is the minimum across
 * organisations, respondents and spread, only the weakest dimension(s) (those
 * equal to the final score) are reported — improving a stronger dimension does
 * not raise the level. Returns all-null for a top-level or empty benchmark.
 *
 * Values are signed deltas relative to the current metric: organisations and
 * respondents are positive (how many MORE are needed); largestShare is negative
 * (how many percentage points LOWER it must be).
 *
 * @param {{ companyCount?: number, respondentCount?: number, respondentsPerCompany?: Array<{respondentCount:number}> }} params
 * @returns {BenchmarkNextLevelGaps}
 */
export const getBenchmarkNextLevelGaps = ({ companyCount = 0, respondentCount = 0, respondentsPerCompany = [] }) => {
    const empty = { organisations: null, respondents: null, largestShare: null }

    // No benchmark data at all → nothing to lift.
    if (!respondentCount) return empty

    const organisationScore = getOrganisationScore(companyCount)
    const respondentScore = getRespondentScore(respondentCount)
    const spreadScore = getSpreadScore(respondentsPerCompany)
    const finalScore = Math.min(organisationScore, respondentScore, spreadScore)

    // Already at the strongest level → nothing to lift.
    if (finalScore >= 3) return empty

    const nextScore = finalScore + 1
    const gaps = { ...empty }

    if (organisationScore === finalScore) {
        gaps.organisations = ORGANISATIONS_FOR_SCORE[nextScore] - companyCount
    }
    if (respondentScore === finalScore) {
        gaps.respondents = RESPONDENTS_FOR_SCORE[nextScore] - respondentCount
    }
    if (spreadScore === finalScore) {
        const largestSharePct = getLargestShareFraction(respondentsPerCompany) * 100
        gaps.largestShare = Math.round((MAX_SHARE_PCT_FOR_SCORE[nextScore] - largestSharePct) * 10) / 10
    }

    return gaps
}

// Client-facing surfaces only show a benchmark from 'Sterke benchmark' (2) up.
// Weaker benchmarks (Redelijk, Zeer zwak) stay visible on the superTenant
// monitor card and admin pages, which read strength directly.
export const MIN_CLIENT_STRENGTH_SCORE = 2

/**
 * Whether a benchmark result is strong enough to show to client organisations.
 *
 * @param {{ benchmarkGrowth?: number|null, companyCount?: number, respondentCount?: number, respondentsPerCompany?: Array }|null} benchmark - a msiBenchmark/participationLadderBenchmark response
 * @returns {boolean}
 */
export const isBenchmarkStrongEnough = (benchmark) => {
    if (!benchmark || benchmark.benchmarkGrowth == null) return false
    return getBenchmarkStrength({
        companyCount: benchmark.companyCount,
        respondentCount: benchmark.respondentCount,
        respondentsPerCompany: benchmark.respondentsPerCompany,
    }).finalScore >= MIN_CLIENT_STRENGTH_SCORE
}

export default getBenchmarkStrength
